import { addDays, addWeeks, addMonths, addYears } from "date-fns";
import { calculateGpsFees, calculateTotalAmountWithGpsFees } from "./gpsFeeCalculations";

export const calculateLoanPayments = (
  loanProduct,
  principal,
  startDate = new Date(),
  gpsTrackers = null
) => {
  console.log('DEBUG CALC: Start date received in calculation function:', startDate);
  console.log('DEBUG CALC: Start date type:', typeof startDate);
  console.log('DEBUG CALC: Is startDate a Date object?', startDate instanceof Date);
  // Constants for time unit conversions
  const daysPerUnit = {
    days: 1,
    weeks: 7,
    months: 30.44, // Average days per month (365.25 / 12)
    years: 365.25, // Average days per year
  };

  const daysPerFrequency = {
    daily: 1,
    weekly: 7,
    bi_weekly: 14,
    "3_weeks": 21,
    monthly: 30.44,
    yearly: 365.25,
  };

  const periodsPerYear = {
    daily: 365,
    weekly: 52,
    bi_weekly: 26, // 52 weeks / 2
    "3_weeks": 17.39,
    monthly: 12,
    yearly: 1,
  };

  // Destructure loan product properties
  const {
    interest_type,
    interest_rate,
    term_unit,
    loan_term,
    payment_frequency,
    simple_interest_method,
    compound_frequency,
  } = loanProduct;

  // Step 1: Calculate next payment date
  console.log('DEBUG CALC: Payment frequency:', payment_frequency);
  console.log('DEBUG CALC: Start date for calculation:', startDate);
  
  let next_payment_date;
  switch (payment_frequency) {
    case "daily":
      next_payment_date = addDays(startDate, 1);
      break;
    case "weekly":
      next_payment_date = addWeeks(startDate, 1);
      break;
    case "bi_weekly":
      next_payment_date = addWeeks(startDate, 2);
      break;
    case "monthly":
      next_payment_date = addMonths(startDate, 1);
      break;
    case "yearly":
      next_payment_date = addYears(startDate, 1);
      break;
    default:
      throw new Error(`Unsupported payment_frequency: ${payment_frequency}`);
  }
  
  console.log('DEBUG CALC: Calculated next payment date:', next_payment_date);

  // Step 2: Calculate number of payments
  const term_in_days = loan_term * daysPerUnit[term_unit];
  const payment_interval_in_days = daysPerFrequency[payment_frequency];
  const number_of_payments = Math.ceil(term_in_days / payment_interval_in_days);

  // Step 3: Calculate payment amount based on interest type
  let payment_amount;
  let total_interest = 0;
  let total_amount = 0;

  if (interest_type === "simple") {
    // Simple interest calculation (default behavior for simple loans)
    total_interest = principal * (interest_rate / 100);
    total_amount = Number(principal) + Number(total_interest);
    payment_amount = total_amount / number_of_payments;
  } else if (interest_type === "compound") {
    // Compound interest calculation
    const m = periodsPerYear[compound_frequency] || 12; // Default to monthly compounding
    const p = periodsPerYear[payment_frequency];
    const r_annual = interest_rate / 100;
    const effective_rate_per_payment = Math.pow(1 + r_annual / m, m / p) - 1;

    const r = effective_rate_per_payment;
    const n = number_of_payments;
    payment_amount =
      (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
    total_amount = payment_amount * n;
    total_interest = total_amount - principal;
  } else {
    // Default to simple interest if type is not recognized
    console.warn(
      `Unrecognized interest_type: ${interest_type}, defaulting to simple interest`
    );
    total_interest = principal * (interest_rate / 100);
    total_amount = Number(principal) + Number(total_interest);
    payment_amount = total_amount / number_of_payments;
  }

  // Step 4: Calculate GPS fees if trackers are provided
  let gpsFeeData = null;
  let totalAmountWithGps = total_amount;
  
  if (gpsTrackers && Array.isArray(gpsTrackers) && gpsTrackers.length > 0) {
    gpsFeeData = calculateGpsFees(gpsTrackers);
    totalAmountWithGps = calculateTotalAmountWithGpsFees(total_amount, gpsFeeData.totalFee);
  }

  // Step 5: Return result with all calculated values
  return {
    next_payment_date,
    next_payment_amount: Number(payment_amount.toFixed(2)),
    total_amount_due: Number(total_amount.toFixed(2)),
    total_amount_with_gps: Number(totalAmountWithGps.toFixed(2)),
    interest_amount: Number(total_interest.toFixed(2)),
    number_of_payments,
    principal: Number(principal),
    payment_schedule: (() => {
      if (!loanProduct?.auto_split_payments || number_of_payments <= 0) {
        return null;
      }

      const roundTwo = (value) => Number(value.toFixed(2));
      const basePayment = roundTwo(total_amount / number_of_payments);
      const payments = [];

      const interestPerPayment =
        interest_type === "simple"
          ? roundTwo(total_interest / number_of_payments)
          : null;
      const principalPerPayment =
        interest_type === "simple"
          ? roundTwo(principal / number_of_payments)
          : null;

      let balance = total_amount;
      let currentDate = startDate;

      for (let i = 0; i < number_of_payments; i += 1) {
        let dueDate;

        switch (payment_frequency) {
          case "daily":
            dueDate = addDays(currentDate, 1);
            break;
          case "weekly":
            dueDate = addWeeks(currentDate, 1);
            break;
          case "bi_weekly":
            dueDate = addWeeks(currentDate, 2);
            break;
          case "3_weeks":
            dueDate = addWeeks(currentDate, 3);
            break;
          case "monthly":
            dueDate = addMonths(currentDate, 1);
            break;
          case "yearly":
            dueDate = addYears(currentDate, 1);
            break;
          default:
            dueDate = addMonths(currentDate, 1);
        }

        const isLast = i === number_of_payments - 1;
        const scheduledAmount = isLast
          ? roundTwo(total_amount - basePayment * (number_of_payments - 1))
          : basePayment;

        const scheduledInterest = interestPerPayment
          ? isLast
            ? roundTwo(total_interest - interestPerPayment * (number_of_payments - 1))
            : interestPerPayment
          : null;

        const scheduledPrincipal =
          principalPerPayment && scheduledInterest !== null
            ? roundTwo(scheduledAmount - scheduledInterest)
            : null;

        balance = roundTwo(balance - scheduledAmount);

        payments.push({
          installment: i + 1,
          due_date: dueDate.toISOString(),
          amount: scheduledAmount,
          principal: scheduledPrincipal,
          interest: scheduledInterest,
          remaining_balance: balance,
        });

        currentDate = dueDate;
      }

      return payments;
    })(),
    gps_fees: gpsFeeData ? {
      total_fee: gpsFeeData.totalFee,
      tracker_count: gpsFeeData.summary.totalTrackers,
      total_months_charged: gpsFeeData.summary.totalMonthsCharged,
      breakdown: gpsFeeData.trackerFees
    } : null
  };
};

export const calculatePrincipalFromPayment = (
  loanProduct,
  paymentAmount,
  overrideInterestRate = null
) => {
  if (!loanProduct) throw new Error("Loan product is required");
  if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
    throw new Error("Invalid payment amount");
  }

  const daysPerUnit = {
    days: 1,
    weeks: 7,
    months: 30.44,
    years: 365.25,
  };

  const daysPerFrequency = {
    daily: 1,
    weekly: 7,
    bi_weekly: 14,
    "3_weeks": 21,
    monthly: 30.44,
    yearly: 365.25,
  };

  const periodsPerYear = {
    daily: 365,
    weekly: 52,
    bi_weekly: 26,
    "3_weeks": 17.39,
    monthly: 12,
    yearly: 1,
  };

  const {
    interest_type,
    interest_rate,
    term_unit,
    loan_term,
    payment_frequency,
    compound_frequency,
  } = loanProduct;

  let rate =
    overrideInterestRate !== null && overrideInterestRate !== undefined
      ? Number(overrideInterestRate)
      : Number(interest_rate || 0);
  if (!Number.isFinite(rate)) rate = 0;

  const termInDays = loan_term * daysPerUnit[term_unit];
  const paymentIntervalInDays = daysPerFrequency[payment_frequency];
  const numberOfPayments = Math.ceil(termInDays / paymentIntervalInDays);

  if (!Number.isFinite(numberOfPayments) || numberOfPayments <= 0) {
    throw new Error("Invalid number of payments");
  }

  if (interest_type === "compound") {
    const m = periodsPerYear[compound_frequency] || 12;
    const p = periodsPerYear[payment_frequency];
    const rAnnual = rate / 100;
    const r = Math.pow(1 + rAnnual / m, m / p) - 1;

    if (!Number.isFinite(r) || r === 0) {
      return paymentAmount * numberOfPayments;
    }

    const rPow = Math.pow(1 + r, numberOfPayments);
    return (paymentAmount * (rPow - 1)) / (r * rPow);
  }

  return (paymentAmount * numberOfPayments) / (1 + rate / 100);
};
